import { useEffect, useRef, useState } from "react";
import type { PickupMapState, PickupZoneId } from "../core/pickup-map";

export function usePickupMap(deviceId: string, enabled = true) {
	const [data, setData] = useState<PickupMapState | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [revision, setRevision] = useState(0);
	const request = useRef<AbortController | null>(null);
	const generation = useRef(0);
	const writing = useRef(false);

	useEffect(() => {
		if (!enabled) return;
		let disposed = false;
		async function load() {
			if (writing.current) return;
			setLoading(true);
			request.current?.abort();
			const controller = new AbortController();
			request.current = controller;
			const version = ++generation.current;
			try {
				const response = await fetch(`/api/pickup/map?deviceId=${encodeURIComponent(deviceId)}`, { signal: controller.signal });
				if (!response.ok) throw new Error("Map unavailable");
				const body = await response.json() as { map: PickupMapState | null };
				if (disposed || version !== generation.current) return;
				setData(body.map);
				setError(null);
			} catch {
				if (disposed || controller.signal.aborted || version !== generation.current) return;
				setData(null);
				setError("Map unavailable");
			} finally {
				if (!disposed && version === generation.current) setLoading(false);
			}
		}
		void load();
		const visible = () => { if (document.visibilityState === "visible") void load(); };
		const timer = window.setInterval(visible, 15_000);
		document.addEventListener("visibilitychange", visible);
		return () => {
			disposed = true;
			++generation.current;
			request.current?.abort();
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", visible);
		};
	}, [deviceId, revision, enabled]);

	async function setSpot(zoneId: PickupZoneId | null) {
		if (!enabled || !data || writing.current) return;
		writing.current = true;
		setSaving(true);
		setLoading(false);
		request.current?.abort();
		const controller = new AbortController();
		request.current = controller;
		const version = ++generation.current;
		try {
			const response = await fetch("/api/pickup/spot", {
				method: "POST", signal: controller.signal,
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ deviceId, pickupId: data.pickupId, zoneId, expectedAtMs: data.spotChangedAtMs }),
			});
			if (!response.ok && response.status !== 409) throw new Error("Pickup spot not saved. Refresh before trying again.");
			const body = await response.json() as { map: PickupMapState | null };
			if (version !== generation.current) return;
			setData(body.map);
			setError(response.status === 409 ? "Pickup changed. Review the current spot before changing it." : null);
		} catch {
			if (controller.signal.aborted || version !== generation.current) return;
			setData(null);
			setError("Pickup spot not saved. Refresh before trying again.");
		} finally {
			writing.current = false;
			if (version === generation.current) setSaving(false);
		}
	}
	return { data, error, loading, saving, setSpot, refresh: () => setRevision(value => value + 1) };
}